/**
 * deviceCapabilityTelemetry — stamps every PostHog event with what the device
 * is actually capable of, and with what the touch-target CSS actually resolved
 * to on that device.
 *
 * WHY THIS EXISTS
 * ---------------
 * The 2026-09-02 unresponsive-UI investigation found dead-click rate scaling by
 * platform on the SAME elements: 0.65/session desktop web, 2.13/session mobile
 * web, 4.06/session native app. The leading untested hypothesis is that the
 * platform-wide 44px touch-target rule in index.css — a bare
 * `@media (pointer: coarse)` block keyed on the `button`, `[role="button"]`,
 * `a`, `input[type=checkbox|radio]` and `select` ELEMENT selectors — may not be
 * matching inside the Android WebView, leaving it silently inert on exactly the
 * platform with the worst dead-click rate.
 *
 * That hypothesis is not answerable from stored events. PostHog already
 * captures $browser_version (WebView version), $device_model and $web_vitals
 * automatically — verified against live event data, not assumed — so none of
 * those are duplicated here. What is genuinely missing is whether
 * `pointer: coarse` MATCHES, what a real rendered control's computed height
 * actually IS, and the network/CPU/memory class the session is running under.
 *
 * This is permanent instrumentation, not a one-off investigation event: the
 * whole point is that a future $dead_click can be sliced by these properties.
 *
 * WHAT IT SETS
 * ------------
 * Registered as PostHog SUPER PROPERTIES (`posthog.register`), attached to
 * every subsequent event from this client. That is deliberate and is the same
 * reasoning as appVersion.ts: §20 restricts new *capture call sites* to
 * ComprehensiveErrorBoundary + main.tsx, and this adds none — it only enriches
 * events those existing sites already send.
 *
 *   pointer_coarse             matchMedia('(pointer: coarse)').matches
 *   pointer_fine               matchMedia('(pointer: fine)').matches
 *   hover_capable              matchMedia('(hover: hover)').matches
 *   button_min_height_px       computed height of a REAL probe <button>
 *   select_item_min_height_px  computed height of a REAL probe SelectItem
 *   connection_effective_type  navigator.connection.effectiveType
 *   connection_downlink_mbps   navigator.connection.downlink
 *   connection_rtt_ms          navigator.connection.rtt
 *   device_memory_gb           navigator.deviceMemory
 *   hardware_concurrency       navigator.hardwareConcurrency
 *
 * The last five are NULL on iOS Safari / iOS WebView, where these APIs do not
 * exist. That is the honest answer and it is left as null — no fallback guess,
 * because a guessed downlink is worse than a known absence.
 *
 * WHY THE HEIGHTS ARE MEASURED, NOT ASSUMED
 * -----------------------------------------
 * This runs at boot, before any route content exists, so there is no real
 * button on the page yet to measure. Two throwaway probe elements are created,
 * measured and removed synchronously within one tick:
 *
 *   - The BUTTON probe is deliberately a bare, class-less <button>. The
 *     coarse-pointer rule targets the element selector, so a class-less button
 *     isolates that rule and nothing else: 44px means the media query matched,
 *     anything smaller means it did not. Giving it a class would measure the
 *     class instead and answer the wrong question.
 *   - The SELECT-ITEM probe carries the exact class string SelectItem renders
 *     (src/components/ui/select.tsx). Note that SelectItem enforces its own
 *     `min-h-[44px]` Tailwind class and does NOT depend on the media query, so
 *     these two numbers are expected to DIVERGE on a device where the media
 *     query is inert — which is precisely the signal being looked for.
 *
 * Both probes are `position: fixed` and off-screen, so they are outside normal
 * flow and cannot shift layout, and `visibility: hidden` rather than
 * `display: none` because a display:none element has no layout and would
 * measure as 0. They are removed in a `finally`, so a throw mid-measurement
 * cannot leak a node into the DOM.
 *
 * Creating elements and assigning via CSSOM (`el.style.x = …`) is not governed
 * by CSP, and this project's style-src carries 'unsafe-inline' regardless.
 */
import posthog from 'posthog-js';

/** Runs at most once per tab. Mirrors main.tsx's __behtari_chunk_recovery flag. */
const SESSION_KEY = '__behtari_device_capability_registered';

/**
 * The exact class string SelectItem renders (src/components/ui/select.tsx).
 * Kept verbatim so the probe measures what a real dropdown option measures. If
 * SelectItem's classes change, this copy goes stale and the number stops
 * describing the live component — update both together.
 */
const SELECT_ITEM_CLASSNAME =
  'relative flex w-full cursor-default select-none items-center rounded-sm min-h-[44px] py-2 pl-8 pr-2 text-sm outline-none';

/** Safe no-op unless PostHog actually initialised (dev, or no key). */
function register(props: Record<string, unknown>): void {
  try {
    if (posthog.__loaded) posthog.register(props);
  } catch {
    /* telemetry must never throw into app startup */
  }
}

function matches(query: string): boolean {
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

/**
 * Renders `element` off-screen, reads its computed height, and always removes
 * it again. Returns null if the measurement could not be taken at all rather
 * than reporting a misleading 0.
 */
function measureOffscreenHeight(element: HTMLElement): number | null {
  const { style } = element;
  style.position = 'fixed';
  style.top = '-9999px';
  style.left = '-9999px';
  style.visibility = 'hidden';
  style.pointerEvents = 'none';

  try {
    document.body.appendChild(element);
    const height = parseFloat(window.getComputedStyle(element).height);
    return Number.isFinite(height) ? height : null;
  } catch {
    return null;
  } finally {
    element.remove();
  }
}

function measureButtonMinHeight(): number | null {
  const probe = document.createElement('button');
  probe.type = 'button';
  probe.setAttribute('aria-hidden', 'true');
  probe.tabIndex = -1;
  // Non-empty so the box is not degenerate; min-height still dominates.
  probe.textContent = '.';
  return measureOffscreenHeight(probe);
}

function measureSelectItemMinHeight(): number | null {
  const probe = document.createElement('div');
  probe.setAttribute('role', 'option');
  probe.setAttribute('aria-hidden', 'true');
  probe.className = SELECT_ITEM_CLASSNAME;
  probe.textContent = '.';
  return measureOffscreenHeight(probe);
}

/**
 * Call once from main.tsx, immediately after posthog.init(). Never throws, runs
 * synchronously (the DOM work is two elements' worth), and does nothing at all
 * on a second call within the same tab.
 */
export function registerDeviceCapabilityTelemetry(): void {
  try {
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Private-mode sessionStorage can throw. Registering twice is harmless
    // (super properties are idempotent), so proceed rather than losing the
    // signal entirely.
  }

  try {
    // Not in lib.dom: NetworkInformation and deviceMemory are still
    // non-standard, and absent entirely on iOS. Read defensively, report null.
    const nav = navigator as Navigator & {
      connection?: { effectiveType?: string; downlink?: number; rtt?: number };
      deviceMemory?: number;
    };
    const connection = nav.connection;

    register({
      pointer_coarse: matches('(pointer: coarse)'),
      pointer_fine: matches('(pointer: fine)'),
      hover_capable: matches('(hover: hover)'),
      button_min_height_px: measureButtonMinHeight(),
      select_item_min_height_px: measureSelectItemMinHeight(),
      connection_effective_type: connection?.effectiveType ?? null,
      connection_downlink_mbps: connection?.downlink ?? null,
      connection_rtt_ms: connection?.rtt ?? null,
      device_memory_gb: nav.deviceMemory ?? null,
      hardware_concurrency: navigator.hardwareConcurrency ?? null,
    });
  } catch {
    /* telemetry must never throw into app startup */
  }
}

/** Test-only: clears the once-per-session guard. */
export function __resetDeviceCapabilityForTests(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* noop */
  }
}
